import React, { useState, useEffect, useContext, createContext, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, refreshFileToken, clearFileToken } from "../shared/config";
import { clearNavSession } from "../navigation/navPref";
import { rattacherAbonnementPush } from "../shared/pushSubscription";

const CLE_CENTRE_FENETRE = 'op_centre_fenetre';
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [centres, setCentres] = useState([]);
  const [centreActif, setCentreActif] = useState(null);
  const [loading, setLoading] = useState(true);
  const maxRetries = 3;

  // Centre propre à CETTE fenêtre : envoyé au serveur à chaque requête (deux fenêtres = deux centres possibles)
  const appliquerCentre = (c) => {
    setCentreActif(c);
    if (c) {
      sessionStorage.setItem(CLE_CENTRE_FENETRE, c.id);
      axios.defaults.headers.common['X-Centre-Id'] = c.id;
    }
  };

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const config = error.config;
        if (error.response?.status === 401 || error.response?.status === 403) {
          return Promise.reject(error);
        }
        if (!config._retryCount) {
          config._retryCount = 0;
        }
        if (config._retryCount < 2 && (!error.response || error.response.status >= 500)) {
          config._retryCount += 1;
          console.log(`Retry ${config._retryCount} pour ${config.url}`);
          await new Promise(resolve => setTimeout(resolve, 1000 * config._retryCount));
          return axios(config);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const cf = sessionStorage.getItem(CLE_CENTRE_FENETRE);
      if (cf) axios.defaults.headers.common['X-Centre-Id'] = cf;
      fetchCurrentUser();
      const id = setInterval(refreshFileToken, 10 * 60 * 1000);
      return () => clearInterval(id);
    } else {
      setLoading(false);
    }
  }, [token]);

  // Rafraîchit les autorisations quand l'onglet redevient visible (droits modifiés par le directeur entre-temps)
  useEffect(() => {
    if (!token) return;
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 60000) return;
      last = Date.now();
      axios.get(`${API}/users/me`, { timeout: 20000 })
        .then(r => setUser(prev => (prev && (JSON.stringify(prev.autorisations) !== JSON.stringify(r.data?.autorisations) || prev.role !== r.data?.role)) ? r.data : prev))
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [token]);

  const retryCountRef = useRef(0);
  
  const fetchCurrentUser = async (currentRetry = 0) => {
    try {
      const response = await axios.get(`${API}/users/me`, { timeout: 60000 });
      await refreshFileToken();
      setUser(response.data);
      retryCountRef.current = 0;
      
      try {
        const centresResponse = await axios.get(`${API}/centres`, { timeout: 60000 });
        const allCentres = centresResponse.data.centres || [];
        const serveurId = response.data.centre_actif_id;
        let liste = allCentres;
        if (response.data.role !== 'Directeur') {
          const userCentreIds = response.data.centre_ids || (response.data.centre_id ? [response.data.centre_id] : []);
          liste = allCentres.filter(c => userCentreIds.includes(c.id));
        }
        setCentres(liste);
        const fenetre = sessionStorage.getItem(CLE_CENTRE_FENETRE);
        const actif = liste.find(c => c.id === fenetre) || liste.find(c => c.id === serveurId) || liste[0] || null;
        // Centre enregistré invalide → on le remplace (centre par défaut des nouvelles fenêtres)
        if (actif && !liste.some(c => c.id === serveurId)) {
          await axios.post(`${API}/centres/${actif.id}/switch`).catch(() => {});
        }
        if (actif && actif.id !== response.data.centre_actif_id) setUser({ ...response.data, centre_actif_id: actif.id });
        appliquerCentre(actif);
        rattacherAbonnementPush(actif?.id || null);
      } catch (centreError) {
        console.warn('Erreur chargement centres:', centreError);
      }
      
      setLoading(false);
    } catch (error) {
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      if (isAuthError) {
        // Jeton expiré/invalide au chargement : cas normal → retour au login sans bruit
        console.log('Session expirée — reconnexion nécessaire');
        setLoading(false);
        logout();
        return;
      }
      console.error(`Erreur récupération utilisateur (tentative ${currentRetry + 1}/${maxRetries}):`, error);
      const isNetworkOrServerError = !error.response || error.response.status >= 500;
      
      if (isNetworkOrServerError && currentRetry < maxRetries) {
        const nextRetry = currentRetry + 1;
        const delay = 1500 * nextRetry;
        console.log(`Retry ${nextRetry}/${maxRetries} dans ${delay/1000}s...`);
        if (nextRetry === 2) {
          toast.info('Connexion en cours, veuillez patienter...');
        }
        setTimeout(() => fetchCurrentUser(nextRetry), delay);
        return;
      }
      setLoading(false);
      logout();
    }
  };

  const getDeviceId = () => {
    let id = localStorage.getItem('mfa_device_id');
    if (!id) {
      id = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)) +
           (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
      localStorage.setItem('mfa_device_id', id);
    }
    return id;
  };

  const finalizeSession = (data, centreId = null) => {
    const { access_token, user: userData, centres: userCentres } = data;
    setToken(access_token);
    setUser(userData);
    setCentres(userCentres || []);
    if (userCentres && userCentres.length > 0) {
      const voulu = centreId || userData?.centre_actif_id;
      const actif = voulu ? userCentres.find(c => c.id === voulu) : null;
      appliquerCentre(actif || userCentres[0]);
    }
    clearNavSession();
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'STORE_TOKEN', token: access_token });
    }
    rattacherAbonnementPush(centreId || userData?.centre_actif_id || null);
  };

  const login = async (email, password, centreId = null) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email, password, centre_id: centreId, device_id: getDeviceId()
      });
      // Compte protégé par MFA : étape 2 requise (aucun jeton délivré ici)
      if (response.data?.mfa_required) {
        return { mfaRequired: true, challengeToken: response.data.challenge_token };
      }
      finalizeSession(response.data, centreId);
      toast.success('Connexion réussie !');
      return { mfaRequired: false };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') throw error;
      toast.error(detail || 'Email ou mot de passe incorrect');
      throw error;
    }
  };

  const completeMfa = async (challengeToken, code, { rememberDevice = false, centreId = null } = {}) => {
    const response = await axios.post(`${API}/auth/mfa/complete`, {
      challenge_token: challengeToken, code,
      remember_device: rememberDevice, device_id: getDeviceId(), centre_id: centreId,
    });
    finalizeSession(response.data, centreId);
    toast.success('Connexion réussie !');
    return true;
  };

  const switchCentre = async (centreId) => {
    try {
      const response = await axios.post(`${API}/centres/${centreId}/switch`);
      const newCentre = centres.find(c => c.id === centreId);
      appliquerCentre(newCentre);
      setUser(prev => ({ ...prev, centre_actif_id: centreId }));
      toast.success(response.data.message);
      window.location.reload();
    } catch (error) {
      toast.error('Erreur lors du changement de centre');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setCentres([]);
    setCentreActif(null);
    localStorage.removeItem('token');
    localStorage.removeItem('op_doctipro_patient');
    localStorage.removeItem('op_doctipro_texte');
    clearFileToken();
    clearNavSession();
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Centre-Id'];
    sessionStorage.removeItem(CLE_CENTRE_FENETRE);
    toast.success('Déconnexion réussie');
  };

  // Auto-déconnexion après X minutes d'inactivité (configurée par le Directeur, par employé)
  const inactivityRef = useRef(null);
  useEffect(() => {
    const minutes = user?.auto_logout_minutes;
    if (!user || !minutes || minutes <= 0) return;
    const reset = () => {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => {
        toast.info(`Déconnexion automatique après ${minutes} min d'inactivité`);
        logout();
      }, minutes * 60 * 1000);
    };
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last > 5000) { last = now; reset(); }
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    reset();
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
    };
  }, [user?.id, user?.auto_logout_minutes]);

  return (
    <AuthContext.Provider value={{ 
      user, setUser, token, setToken, 
      centres, setCentres, centreActif, setCentreActif,
      login, logout, switchCentre, loading, completeMfa 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, useAuth, AuthProvider };
